/* ---------------------------------------------------------------------------
 * Geographic helpers for the Region radar. We only have COUNTRY-level location
 * (GeoIP gives a 2-letter code, no per-server coordinates), so the radar plots
 * one dot per country at its centroid and the radius filter works on great-circle
 * distance from the visitor to each country's centroid. Good enough for "servers
 * near me"; per-server city-level geo is a possible later upgrade.
 * ------------------------------------------------------------------------- */

export interface Centroid { lat: number; lon: number; name: string }

// ISO 3166-1 alpha-2 -> approximate country centroid + display name. Covers every
// country that realistically hosts (or could host) a DayZ server, plus broad spares.
export const COUNTRY: Record<string, Centroid> = {
  AD: { lat: 42.5, lon: 1.5, name: 'Andorra' },
  AE: { lat: 24.0, lon: 54.0, name: 'United Arab Emirates' },
  AF: { lat: 33.0, lon: 65.0, name: 'Afghanistan' },
  AL: { lat: 41.0, lon: 20.0, name: 'Albania' },
  AM: { lat: 40.0, lon: 45.0, name: 'Armenia' },
  AO: { lat: -12.5, lon: 18.5, name: 'Angola' },
  AR: { lat: -34.0, lon: -64.0, name: 'Argentina' },
  AT: { lat: 47.3, lon: 13.3, name: 'Austria' },
  AU: { lat: -25.0, lon: 133.0, name: 'Australia' },
  AZ: { lat: 40.5, lon: 47.5, name: 'Azerbaijan' },
  BA: { lat: 44.0, lon: 18.0, name: 'Bosnia & Herzegovina' },
  BD: { lat: 24.0, lon: 90.0, name: 'Bangladesh' },
  BE: { lat: 50.8, lon: 4.5, name: 'Belgium' },
  BG: { lat: 43.0, lon: 25.0, name: 'Bulgaria' },
  BH: { lat: 26.0, lon: 50.5, name: 'Bahrain' },
  BO: { lat: -17.0, lon: -65.0, name: 'Bolivia' },
  BR: { lat: -10.0, lon: -55.0, name: 'Brazil' },
  BY: { lat: 53.7, lon: 28.0, name: 'Belarus' },
  CA: { lat: 56.0, lon: -98.0, name: 'Canada' },
  CH: { lat: 46.8, lon: 8.2, name: 'Switzerland' },
  CL: { lat: -30.0, lon: -71.0, name: 'Chile' },
  CN: { lat: 35.0, lon: 105.0, name: 'China' },
  CO: { lat: 4.0, lon: -73.0, name: 'Colombia' },
  CR: { lat: 10.0, lon: -84.0, name: 'Costa Rica' },
  CY: { lat: 35.0, lon: 33.0, name: 'Cyprus' },
  CZ: { lat: 49.8, lon: 15.5, name: 'Czechia' },
  DE: { lat: 51.2, lon: 10.4, name: 'Germany' },
  DK: { lat: 56.0, lon: 10.0, name: 'Denmark' },
  DO: { lat: 19.0, lon: -70.7, name: 'Dominican Republic' },
  DZ: { lat: 28.0, lon: 3.0, name: 'Algeria' },
  EC: { lat: -1.5, lon: -78.0, name: 'Ecuador' },
  EE: { lat: 59.0, lon: 26.0, name: 'Estonia' },
  EG: { lat: 27.0, lon: 30.0, name: 'Egypt' },
  ES: { lat: 40.0, lon: -4.0, name: 'Spain' },
  FI: { lat: 64.0, lon: 26.0, name: 'Finland' },
  FR: { lat: 46.6, lon: 2.4, name: 'France' },
  GB: { lat: 54.0, lon: -2.0, name: 'United Kingdom' },
  GE: { lat: 42.0, lon: 43.5, name: 'Georgia' },
  GR: { lat: 39.0, lon: 22.0, name: 'Greece' },
  GT: { lat: 15.5, lon: -90.3, name: 'Guatemala' },
  HK: { lat: 22.3, lon: 114.2, name: 'Hong Kong' },
  HR: { lat: 45.1, lon: 15.5, name: 'Croatia' },
  HU: { lat: 47.0, lon: 20.0, name: 'Hungary' },
  ID: { lat: -2.5, lon: 118.0, name: 'Indonesia' },
  IE: { lat: 53.0, lon: -8.0, name: 'Ireland' },
  IL: { lat: 31.5, lon: 34.8, name: 'Israel' },
  IN: { lat: 22.0, lon: 79.0, name: 'India' },
  IQ: { lat: 33.0, lon: 44.0, name: 'Iraq' },
  IR: { lat: 32.0, lon: 53.0, name: 'Iran' },
  IS: { lat: 65.0, lon: -18.0, name: 'Iceland' },
  IT: { lat: 42.8, lon: 12.8, name: 'Italy' },
  JO: { lat: 31.0, lon: 36.0, name: 'Jordan' },
  JP: { lat: 36.0, lon: 138.0, name: 'Japan' },
  KE: { lat: 0.0, lon: 38.0, name: 'Kenya' },
  KR: { lat: 36.5, lon: 127.8, name: 'South Korea' },
  KW: { lat: 29.3, lon: 47.6, name: 'Kuwait' },
  KZ: { lat: 48.0, lon: 68.0, name: 'Kazakhstan' },
  LB: { lat: 33.8, lon: 35.8, name: 'Lebanon' },
  LT: { lat: 55.2, lon: 24.0, name: 'Lithuania' },
  LU: { lat: 49.8, lon: 6.1, name: 'Luxembourg' },
  LV: { lat: 57.0, lon: 25.0, name: 'Latvia' },
  MA: { lat: 32.0, lon: -6.0, name: 'Morocco' },
  MD: { lat: 47.0, lon: 28.5, name: 'Moldova' },
  ME: { lat: 42.7, lon: 19.4, name: 'Montenegro' },
  MK: { lat: 41.6, lon: 21.7, name: 'North Macedonia' },
  MN: { lat: 46.0, lon: 105.0, name: 'Mongolia' },
  MT: { lat: 35.9, lon: 14.4, name: 'Malta' },
  MX: { lat: 23.0, lon: -102.0, name: 'Mexico' },
  MY: { lat: 4.0, lon: 109.0, name: 'Malaysia' },
  NG: { lat: 9.0, lon: 8.0, name: 'Nigeria' },
  NL: { lat: 52.2, lon: 5.3, name: 'Netherlands' },
  NO: { lat: 62.0, lon: 10.0, name: 'Norway' },
  NZ: { lat: -42.0, lon: 174.0, name: 'New Zealand' },
  OM: { lat: 21.0, lon: 57.0, name: 'Oman' },
  PA: { lat: 9.0, lon: -80.0, name: 'Panama' },
  PE: { lat: -10.0, lon: -76.0, name: 'Peru' },
  PH: { lat: 13.0, lon: 122.0, name: 'Philippines' },
  PK: { lat: 30.0, lon: 70.0, name: 'Pakistan' },
  PL: { lat: 52.0, lon: 19.0, name: 'Poland' },
  PT: { lat: 39.5, lon: -8.0, name: 'Portugal' },
  PY: { lat: -23.0, lon: -58.0, name: 'Paraguay' },
  QA: { lat: 25.3, lon: 51.2, name: 'Qatar' },
  RO: { lat: 46.0, lon: 25.0, name: 'Romania' },
  RS: { lat: 44.0, lon: 21.0, name: 'Serbia' },
  RU: { lat: 60.0, lon: 90.0, name: 'Russia' },
  SA: { lat: 24.0, lon: 45.0, name: 'Saudi Arabia' },
  SE: { lat: 62.0, lon: 15.0, name: 'Sweden' },
  SG: { lat: 1.35, lon: 103.8, name: 'Singapore' },
  SI: { lat: 46.1, lon: 14.8, name: 'Slovenia' },
  SK: { lat: 48.7, lon: 19.5, name: 'Slovakia' },
  TH: { lat: 15.0, lon: 101.0, name: 'Thailand' },
  TN: { lat: 34.0, lon: 9.0, name: 'Tunisia' },
  TR: { lat: 39.0, lon: 35.0, name: 'Turkey' },
  TW: { lat: 23.7, lon: 121.0, name: 'Taiwan' },
  UA: { lat: 49.0, lon: 32.0, name: 'Ukraine' },
  US: { lat: 39.0, lon: -98.0, name: 'United States' },
  UY: { lat: -33.0, lon: -56.0, name: 'Uruguay' },
  UZ: { lat: 41.0, lon: 64.0, name: 'Uzbekistan' },
  VE: { lat: 8.0, lon: -66.0, name: 'Venezuela' },
  VN: { lat: 16.0, lon: 108.0, name: 'Vietnam' },
  ZA: { lat: -29.0, lon: 24.0, name: 'South Africa' },
}

export const countryName = (code: string): string => COUNTRY[code]?.name ?? code
export const hasCentroid = (code: string): boolean => code in COUNTRY

// "Ping for you" estimate, derived from the great-circle distance between the
// VIEWER's country and the SERVER's country. We do this because a browser cannot
// measure real latency to a game server (no UDP/ICMP from JS), and the ping we
// collect is measured from a single monitor location — useless as "your ping".
//
// Physics gives a hard floor: light in fibre travels ~200,000 km/s, so a round
// trip costs ~0.01 ms/km at best; real routing/peering roughly doubles that. We
// use ~0.022 ms/km plus a small last-mile constant, which lands close to typical
// measured pings. It is an ESTIMATE (country-centroid based, so every server in a
// country shares a number) and must always be labelled as one in the UI. Returns
// null when either location is unknown so callers can show "—".
export function estimatePingMs(
  viewerCC: string | null | undefined,
  serverCC: string | null | undefined,
): number | null {
  if (!viewerCC || !serverCC) return null
  const a = COUNTRY[viewerCC.toUpperCase()]
  const b = COUNTRY[serverCC.toUpperCase()]
  if (!a || !b) return null
  const est = 8 + haversineKm(a, b) * 0.022
  // Round to the nearest 5 ms and floor at 15 — signals "approximate", and keeps
  // same-country (centroid distance ~0) from reading as an implausible single digit.
  return Math.max(15, Math.round(est / 5) * 5)
}

// Great-circle distance in km between two lat/lon points.
const R_EARTH = 6371
const toRad = (d: number) => (d * Math.PI) / 180
export function haversineKm(a: { lat: number; lon: number }, b: { lat: number; lon: number }): number {
  const dLat = toRad(b.lat - a.lat), dLon = toRad(b.lon - a.lon)
  const s = Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(a.lat)) * Math.cos(toRad(b.lat)) * Math.sin(dLon / 2) ** 2
  return 2 * R_EARTH * Math.asin(Math.min(1, Math.sqrt(s)))
}

// Equirectangular projection: lon/lat -> x/y in a [0..w] x [0..h] box.
export const projX = (lon: number, w: number) => ((lon + 180) / 360) * w
export const projY = (lat: number, h: number) => ((90 - lat) / 180) * h

// Faint orientation labels (no landmass asset needed) at rough continent centroids.
export const CONTINENT_LABELS: { lat: number; lon: number; text: string }[] = [
  { lat: 48, lon: -100, text: 'N. AMERICA' },
  { lat: -15, lon: -60, text: 'S. AMERICA' },
  { lat: 50, lon: 15, text: 'EUROPE' },
  { lat: 2, lon: 20, text: 'AFRICA' },
  { lat: 45, lon: 90, text: 'ASIA' },
  { lat: -27, lon: 134, text: 'OCEANIA' },
]
